<template>
  <div>
    <div class="mb-5 d-flex justify-center" style="position: relative">
      <table ref="table" class="gantt-table">
        <colgroup>
          <col span="4" />
          <col v-for="i in days.length" :key="i" class="cell-col" />
        </colgroup>
        <thead class="primary">
          <tr class="">
            <th colspan="99" class="pa-4 prepend-header">
              <v-row class="control-selection px-4">
                <v-btn
                  icon
                  :disabled="
                    internalDate
                      ? $dayjs(internalDate).isSameOrBefore(dateFrom)
                      : true
                  "
                  @click="back()"
                >
                  <v-icon color="white">$iconCircleCaretLeft</v-icon>
                </v-btn>
                <div
                  class="px-3 white--text bold"
                  style="font-size: 12px; align-self: center"
                >
                  {{ $t("calendar:gantt.current.label") }}
                </div>
                <v-btn
                  icon
                  :disabled="days.length < maxShowDays"
                  @click="forward()"
                >
                  <v-icon color="white">$iconCircleCaretRight</v-icon>
                </v-btn>
              </v-row>
            </th>
          </tr>
          <tr class="weeks-row">
            <th colspan="4"></th>
            <th
              v-for="(week, i) in weeks"
              :key="i"
              :class="['week']"
              :colspan="week.colspan"
              :style="
                week.firstWeekCalendar ? 'border-left: 1px solid white' : ''
              "
            >
              <template v-if="week.isFullWeek">
                {{ week.weekNumber }}
                <span class="week-text">{{
                  $t("calendar:gantt.week.label")
                }}</span>
              </template>
            </th>
          </tr>
          <tr class="days-row">
            <th colspan="4" class="month">{{ dominativeMonth }}</th>
            <th
              v-for="(day, i) in days"
              :key="i"
              :class="['day', !day.active ? 'day-inactive' : '']"
            >
              {{ day.dayNumber }}
              <div class="day-text">{{ day.text }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="eventItems.length == 0">
            <td colspan="4" />
            <td v-for="i in days.length" :key="i" />
          </tr>
          <template v-else>
            <GanttEventRow
              v-for="(item, i) in eventItems"
              :key="i"
              :item="item"
              :event-index="i"
              @block-over="showEvent(i, $event)"
              @block-out="visibleCard = null"
            >
              <template #cellInfo="{ event }">
                <td
                  colspan="4"
                  style="position: relative; max-width: 115px"
                  @mouseover="showEvent(i, `.cell-${i}-0`)"
                  @mouseout="visibleCard = null"
                >
                  <v-card
                    :to="linkToEvent(event)"
                    :class="['pa-2 ma-1', `cell-${i}-0`]"
                    style=""
                  >
                    <div
                      style="
                        event-pointers: none;
                        font-size: 11px;
                        word-break: break-word;
                      "
                    >
                      {{
                        truncate(event.name, {
                          length: 30
                        })
                      }}
                      <v-icon
                        v-if="event.isHighlighted"
                        class="star-icon"
                        color="star"
                        style="font-size: 15px"
                        >$iconStar</v-icon
                      >
                    </div>
                  </v-card>
                  <div
                    v-show="visibleCard == i"
                    style="z-index: 55; position: relative"
                    :class="`event-card--${i}`"
                    aria-live="polite"
                  >
                    <EventGanttCard :data="event" style="z-index: 56" />
                  </div>
                </td>
              </template>
            </GanttEventRow>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { uniqBy, countBy, truncate, compact } from "lodash-es";
import GanttEventRow from "./GanttEventRow.vue";
import EventGanttCard from "./EventGanttCard.vue";

// Used in functional component EventGanttCard
import AppShowDifficultyLevels from "@/components/elements/AppShows/AppShowDifficultyLevels.vue";
import AppShowCompetitionLevelNames from "@/components/elements/AppShows/event/AppShowCompetitionLevelNames.vue";
import AppShowEventcodes from "@/components/elements/AppShows/event/AppShowEventcodes.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";
import AppShowEventName from "@AppShows/event/AppShowEventName.vue";
import AppShowEventCategories from "@AppShows/event/AppShowEventCategories.vue";
import AppShowProfQualificationsList from "@AppShows/AppShowProfQualificationsList.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

export default {
  name: "AppGantt",
  components: {
    GanttEventRow,
    EventGanttCard,
    // eslint-disable-next-line
    AppShowDifficultyLevels,
    // eslint-disable-next-line
    AppShowCompetitionLevelNames,
    // eslint-disable-next-line
    AppShowEventcodes,
    // eslint-disable-next-line
    AppShowEventStatus,
    // eslint-disable-next-line
    AppShowEventCupComponent,
    // eslint-disable-next-line
    AppShowEventName,
    // eslint-disable-next-line
    AppShowEventCategories,
    // eslint-disable-next-line
    AppShowProfQualificationsList,
    // eslint-disable-next-line
    AppShowEventReferenceCode
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    events: {
      type: Array,
      default: () => []
    },
    dateFrom: {
      type: String,
      default: null
    },
    dateUntil: {
      type: String,
      default: null
    },
    lastItemDate: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      internalDate: null,
      visibleCard: null,
      maxShowDays: 32,
      resizeObserver: null
    };
  },
  computed: {
    truncate: () => truncate,
    weeks() {
      const uniqueWeeks = uniqBy(this.days, "week");
      const weeks = [];
      let firstWeekPushed = false;
      uniqueWeeks.forEach(({ week }) => {
        const filteredDays = this.days.filter(day => day.week == week);
        if (filteredDays.length == 7) {
          let firstWeek = false;
          if (firstWeekPushed === false) {
            firstWeekPushed = true;
            firstWeek = true;
          }
          weeks.push({
            colspan: 7,
            isFullWeek: true,
            weekNumber: week,
            // Theres a problem [colspan="7"]:first-child not working in this table
            firstWeekCalendar: firstWeek
          });
        } else {
          filteredDays.forEach(() =>
            weeks.push({
              colspan: 1,
              isFullWeek: false
            })
          );
        }
      });

      return weeks;
    },
    days() {
      let currentDay = this.$dayjs(this.internalDate);
      const days = [];

      for (let i = 0; i < this.maxShowDays; i++) {
        days.push({
          dayNumber: currentDay.format("DD"),
          text: currentDay.format("ddd"),
          week: currentDay.format("WW"),
          month: currentDay.format("M"),
          iso: currentDay.format("YYYY-MM-DD"),
          isWeekend: [0, 6].includes(currentDay.day()),
          active: false
        });

        currentDay = currentDay.add(1, "days");
        // If its after dateUntil date now, close the loop
        if (this.dateUntil && currentDay.isAfter(this.dateUntil)) i = 9999;
      }

      let dominativeMonth = countBy(days, "month");
      const monthTotalDays = Object.values(dominativeMonth);
      if (monthTotalDays[0] < monthTotalDays[1]) {
        dominativeMonth = Object.keys(dominativeMonth)[1];
      } else {
        dominativeMonth = Object.keys(dominativeMonth)[0];
      }

      return days.map(x => {
        if (x.month == dominativeMonth) x.active = true;
        return x;
      });
    },
    dominativeMonth() {
      let dominativeMonth = countBy(this.days, "month");
      const monthTotalDays = Object.values(dominativeMonth);
      if (monthTotalDays[0] < monthTotalDays[1]) {
        dominativeMonth = Object.keys(dominativeMonth)[1];
      } else {
        dominativeMonth = Object.keys(dominativeMonth)[0];
      }
      // Set is index based
      return this.$dayjs()
        .set("month", dominativeMonth - 1)
        .format("MMMM");
    },
    eventItems() {
      return compact(
        this.events.map(event => {
          const item = { rowCells: [], event: event };
          const start = event.startDate;
          const end = event.endDate;
          this.days.forEach(day => {
            const iso = this.$dayjs(day.iso);
            if (
              (start === end && day.iso === start) ||
              iso.isBetween(start, end) ||
              (iso.isSameOrBefore(end) && iso.isSameOrAfter(start))
            ) {
              item.rowCells.push({
                isWeekend: day.isWeekend,
                used: true
              });
            } else {
              item.rowCells.push({
                isWeekend: day.isWeekend,
                used: false
              });
            }
          });
          if (item.rowCells.some(x => x.used == true)) {
            return item;
          } else return null;
        })
      );
    }
  },
  watch: {
    dateFrom(val) {
      this.internalDate = this.$dayjs(val).format("YYYY-MM-DD");
    }
  },
  created() {
    if (this.dateFrom)
      this.internalDate = this.$dayjs(this.dateFrom).format("YYYY-MM-DD");
    else this.internalDate = this.$dayjs().format("YYYY-MM-DD");
    const Observer = window.ResizeObserver ?? ResizeObserverPolyfill;

    this.resizeObserver = new Observer(() => {
      this.responsiveTable();
    });
  },
  mounted() {
    this.resizeObserver.observe(this.$el);
  },
  methods: {
    showEvent(i, cell) {
      const event = this.$el.querySelector(`.event-card--${i}`);
      if (event) {
        let cellIndex = cell.split("-");
        cellIndex = Number(cellIndex[cellIndex.length - 1]);
        let x = 0;

        if (cell != `.cell-${i}-0` && window.innerWidth >= 728) {
          // Show event card on left side by lowering cellIndex
          const showOnLeftSide =
            this.maxShowDays - (cellIndex - 10) <= 16 ? 10 : 0;

          // Not all cells are equal width sadly
          for (let index = 1; index <= cellIndex - showOnLeftSide; index++) {
            let cell = this.$el.querySelector(`.cell-${i}-${index}`);
            x += Math.floor(cell.offsetWidth);
          }
          x += 5;
          // Move bit closer to block
          if (showOnLeftSide) x += 37;
        }

        event.style.transform = `translateX(${x}px)`;
        this.visibleCard = i;
      }
    },
    forward() {
      this.internalDate = this.$dayjs(this.internalDate).add(1, "weeks");
      if (this.events.length > 25) {
        const lastItemDate = this.$dayjs(
          this.events[this.events.length - 10].startDate
        );
        if (
          this.internalDate.isBetween(lastItemDate, this.dateUntil) &&
          lastItemDate.isSameOrBefore(this.dateUntil)
        )
          this.$emit("loadMore");
      }

      this.internalDate = this.internalDate.format("YYYY-MM-DD");
    },
    back() {
      this.internalDate = this.$dayjs(this.internalDate)
        .subtract(1, "weeks")
        .format("YYYY-MM-DD");
    },
    async responsiveTable() {
      const maxDays = 32;
      if (this.loading === true) return;
      if (this.$vuetify.breakpoint.lgAndUp) {
        this.maxShowDays = maxDays;
        return;
      }

      await this.$nextTick();
      this.$forceUpdate();

      const decreaser = 32;
      const increaser = 32;

      let currentWindowWidth = window.outerWidth - 20; // 20 comes from padding-left and padding-right each being 10px in mobile view
      let tableWidth = 0;

      // If nothing then can't continue
      if (this.$refs.table) {
        tableWidth = this.$refs.table.offsetWidth;
      } else return;

      let currentMaxShowDays = this.maxShowDays;
      let cellIndexes = this.$el.querySelector("table tr td.cell:last-child");
      if (cellIndexes) {
        cellIndexes = Array.from(cellIndexes.classList).filter(x =>
          x.includes("cell-")
        )[0];
        cellIndexes = cellIndexes.split("-");
        cellIndexes = cellIndexes[cellIndexes.length - 1];
      } else {
        cellIndexes = this.maxShowDays;
      }

      if (tableWidth + increaser > currentWindowWidth) {
        for (
          let i = Number(cellIndexes);
          tableWidth >= currentWindowWidth;
          i--
        ) {
          const cellWidth = this.$el.querySelector(`tr td.cell-0-${i}`);
          currentWindowWidth += cellWidth?.offsetWidth ?? 0;
          currentMaxShowDays--;
        }
        this.maxShowDays = currentMaxShowDays;
      } else if (tableWidth < currentWindowWidth - decreaser) {
        let blockLoop = false;
        for (
          let i = Number(cellIndexes);
          tableWidth < currentWindowWidth && !blockLoop;
          i++
        ) {
          //const cellWidth = this.$el.querySelector(`tr td.cell-0-${i}`);
          tableWidth += 31;
          currentMaxShowDays++;
          if (maxDays <= currentMaxShowDays) {
            currentMaxShowDays = maxDays;
            blockLoop = true;
          }
        }
        this.maxShowDays = currentMaxShowDays;
      }
    },
    linkToEvent(event) {
      if (this.isNoncompetition) {
        return {
          name: "NoncompetitionEventProfilePage",
          params: { eventId: event.id }
        };
      }
      if (["results_published", "closed", "archived"].includes(event.status)) {
        return { name: "EventResultsPage", params: { eventId: event.id } };
      } else {
        return { name: "EventProfilePage", params: { eventId: event.id } };
      }
    }
  }
};
</script>
<style lang="scss">
$border: 1px solid white;

table.gantt-table {
  table-layout: fixed;
  border-spacing: 0;
  //width: 100%;
  max-width: 100%;
  col.cell-col {
    width: 30px;
    min-width: 30px;
  }
  thead {
    tr {
      th {
        position: sticky;

        background-color: var(--v-primary-base);
        z-index: 2;
        &.prepend-header {
          background-color: var(--v-primary-base);
          top: 0;
          .control-selection {
            i:before {
              font-size: 24px !important;
            }
          }
          @media (max-width: 876px) {
            top: 48px;
          }
        }
        &.day {
          //border-left: $border;
          top: 67.5px;
          min-width: 27px;
          @media (max-width: 876px) {
            top: 115.5px;
          }
          border-left: $border;
          //width: 28px;
          padding: 4px;
          font-size: 11px;
          color: white !important;
          font-weight: normal;
          .day-text {
            font-weight: bold;
          }
          &.day-inactive {
            border-color: #8d97af;
            color: #8d97af !important;
          }
        }
        &[colspan="4"] {
          top: 59px;
          @media (max-width: 876px) {
            top: 107px;
          }
        }
        &.week {
          &[colspan="7"] {
            border-left: $border;
          }
          top: 58.5px;
          @media (max-width: 876px) {
            top: 106.5px;
          }
          padding: 12px;
          text-align: center;
          font-size: 12px;
          font-weight: normal;
          color: white !important;
          .week-text {
            font-weight: bold;
          }
        }
        &.month {
          min-width: 110px;
          padding: 14px;
          top: 67.5px;
          @media (max-width: 876px) {
            top: 115.5px;
          }
          color: white !important;
          font-weight: bold;
        }
      }

      &.days-row {
        th.day {
          &:last-child {
            border-right: 0;
          }
          &.day-inactive {
            border-color: #8d97af;
            color: #8d97af !important;
          }
        }
      }
    }
  }
  tbody {
    $body-line-color: rgba(233, 238, 250, 1);
    tr {
      td:first-child {
        .event-name {
          position: relative;
          font-size: 12px;
          cursor: pointer;
        }
        border-left: 1px solid transparent;
      }
      td {
        border-right: 1px solid $body-line-color;
        border-left: 1px solid $body-line-color;
      }
      td.loading-row {
        padding: 8px !important;
      }
    }
    tr:last-child {
      td {
        border-bottom: 1px solid $body-line-color;
      }
    }
  }
}
</style>
